import { useEffect, useRef } from "react";

var FORM_ID = import.meta.env.VITE_BLOOMERANG_FORM_ID || "398451712";
var CONTAINER_ID = "bloomerang-persistent-container";

// Ensure the Bloomerang form is injected exactly once into a persistent
// container on document.body. This container survives React mount/unmount
// cycles (modal open/close). The React component just adopts it into its
// own mount point when visible, and releases it back to body (hidden)
// on unmount.
function ensureFormInjected() {
  var container = document.getElementById(CONTAINER_ID);
  if (container) return container;

  // First time: create persistent container + inject Bloomerang snippet
  container = document.createElement("div");
  container.id = CONTAINER_ID;
  container.style.display = "none";
  document.body.appendChild(container);

  // Placeholder script tag — the Bloomerang snippet looks for this by ID
  var placeholder = document.createElement("script");
  placeholder.type = "text/javascript";
  placeholder.id = "bloomerangForm" + FORM_ID;
  container.appendChild(placeholder);

  // Load the verbatim Bloomerang snippet from public/bloomerang/{id}.js
  var snippet = document.createElement("script");
  snippet.type = "text/javascript";
  snippet.src = "/bloomerang/" + FORM_ID + ".js";
  snippet.async = true;
  document.body.appendChild(snippet);

  return container;
}

// Call on page load to preload Bloomerang scripts in the background.
// The form renders hidden until BloomerangForm mounts and adopts it.
export function preload() {
  ensureFormInjected();
}

export default function BloomerangForm({ onPaymentSuccess, onError }) {
  var mountRef = useRef(null);
  var calledRef = useRef(false);

  useEffect(function () {
    if (!mountRef.current) return;

    // Adopt the persistent container into our React mount point
    var container = ensureFormInjected();
    mountRef.current.appendChild(container);
    container.style.display = "block";

    // Reset the success guard so the form can be used again after a
    // previous successful donation (e.g., close modal, reopen, donate again)
    calledRef.current = false;

    // Poll until Bloomerang has installed its Widget.Donation callbacks,
    // then override OnSuccess to bridge into our React tribute-writing flow.
    var hookInterval = setInterval(function () {
      if (
        window.Bloomerang &&
        window.Bloomerang.Widget &&
        window.Bloomerang.Widget.Donation &&
        typeof window.Bloomerang.Widget.Donation.OnSuccess === "function"
      ) {
        clearInterval(hookInterval);

        // Restyle the "Donation" heading (idempotent — check before changing)
        var donationH3 = document.querySelector(".donation-form .section.donation h3");
        if (donationH3 && donationH3.textContent === "Donation") {
          donationH3.textContent = "Choose Your Donation Level";
        }

        // Style donation-level labels: bold the dollar amount (idempotent)
        document.querySelectorAll(".donation-form .section.donation .field.radio .label").forEach(function (span) {
          if (span.querySelector("strong")) return;
          var text = span.textContent;
          var match = text.match(/^(\$[\d,.]+)\s*[-–—]\s*(.+)$/);
          if (match) {
            span.innerHTML = "<strong>" + match[1] + "</strong>  " + match[2];
          }
        });

        // Fix ZIP code field: Bloomerang renders type="number" which strips
        // leading zeros and rejects ZIP+4 format. Switch to type="text".
        var zipField = document.querySelector("#donation-form #zip-code");
        if (zipField && zipField.type === "number") {
          zipField.type = "text";
          zipField.inputMode = "numeric";
        }

        // Override OnSuccess to bridge into our React tribute-writing flow.
        window.Bloomerang.Widget.Donation.OnSuccess = function (response) {
          if (calledRef.current) return;
          calledRef.current = true;

          // Read donor info from Bloomerang's form fields
          var emailEl = document.querySelector("#donation-form #email-address");
          var firstEl = document.querySelector("#donation-form #first-name");
          var lastEl = document.querySelector("#donation-form #last-name");
          var checkedLevel = document.querySelector(
            "#donation-form input[name='donation-level']:checked"
          );

          var donorEmail = emailEl ? emailEl.value : "";
          var firstName = firstEl ? firstEl.value : "";
          var lastName = lastEl ? lastEl.value : "";
          var amount = checkedLevel ? Number(checkedLevel.value) : 35;

          // Determine tier dynamically from available radio values
          var allLevels = document.querySelectorAll(
            "#donation-form input[name='donation-level']"
          );
          var amounts = [];
          allLevels.forEach(function (el) {
            var v = Number(el.value);
            if (!isNaN(v) && v > 0) amounts.push(v);
          });
          amounts.sort(function (a, b) { return a - b; });
          var isHigherTier = amounts.length >= 2 && amount >= amounts[amounts.length - 1];

          onPaymentSuccess({
            donationId: response && response.id,
            donorEmail: donorEmail,
            fromName: (firstName + " " + lastName).trim(),
            amount: amount,
            tier: isHigherTier ? "tier40" : "tier35",
            maxTributes: isHigherTier ? 10 : 5,
          });
        };

        // Hook OnError to surface Bloomerang errors
        var origOnError = window.Bloomerang.Widget.Donation.OnError;
        window.Bloomerang.Widget.Donation.OnError = function (response) {
          origOnError(response);
          if (onError) {
            onError(response && response.Message ? response.Message : "Payment failed");
          }
        };
      }
    }, 200);

    return function () {
      clearInterval(hookInterval);
      // Release the container back to body (hidden) — don't destroy it
      container.style.display = "none";
      document.body.appendChild(container);
    };
  }, [onPaymentSuccess, onError]);

  return <div ref={mountRef} />;
}
